class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
      this.collapseMobileSearch();
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
      MOBILE_EXPANDED: 'mobile-search-expanded',
    };

    // Mobil: søkefeltet ligger skjult bak et ikon i headeren i stedet for å
    // ta en egen rad (Magnus, 2026-08-19). Feltet er nøyaktig det samme
    // elementet som på desktop, bare skjult med CSS, så ikonet gjør ikke
    // annet enn å felle det ut og fokusere det. All panel-logikk under er
    // uendret. Kun header-instansen kobler seg på. I dag er den den eneste
    // som finnes (/search-sidens eget felt er fjernet), men klassen dekker
    // fortsatt context: 'page', og en slik instans skal ikke styre headeren.
    this.mobileSearchToggle = this.isHeaderSearch ? document.querySelector('.header-search-toggle') : null;
    this.mobileSearchToggle?.addEventListener('click', this.toggleMobileSearch.bind(this));
    this.addEventListener('keydown', this.handleEscapeKey.bind(this));

    // Klikk-tilstanden slik den ble rendret ved sideinnlasting, før noe
    // søk har kjørt. Brukes til å nullstille resultat-beholderen når
    // feltet tømmes, ellers blir forrige søks HTML stående (aldri
    // overskrevet siden et tomt søk ikke trigger noe nytt AJAX-kall).
    this.initialResultsHTML = this.resultsContainer?.innerHTML;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get resultsContainer() {
    return document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
  }

  // Redesignet søkepanel (design_handoff_sok): ikke koblet til
  // hamburger-menyen slik det gamle logo-center-mønsteret var, se
  // onFocus/toggleSearchState/clear.
  get isRedesignedPanel() {
    return this.classList.contains('search-panel-root');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      if (!this.isRedesignedPanel) this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    if (this.isRedesignedPanel) {
      // Kryssknappen skal lukke hele panelet, ikke bare tømme og beholde
      // fokus (som ville trigget onFocus på nytt og åpnet klikk-tilstanden
      // igjen med det samme).
      this.input.blur();
      // Uten dette blir forrige søks treff stående i DOM-en, siden et tomt
      // søk aldri trigger et nytt AJAX-kall som ville overskrevet dem.
      if (this.resultsContainer && this.initialResultsHTML !== undefined) {
        this.resultsContainer.innerHTML = this.initialResultsHTML;
      }
    } else {
      this.input.focus();
    }
    this.removeAttribute('results');
    this.toggleSearchState(false);
    // Et ekte klikk på krysset er en eksplisitt lukking, og da skal
    // mobilfeltet trekke seg tilbake til ikonet. Kalles clear() derimot fra
    // onChange fordi brukeren selv har slettet teksten (event === null),
    // skal feltet bli stående, ellers forsvinner det under fingeren midt i
    // et søk.
    if (event) this.collapseMobileSearch();
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[type]', 'product,collection,query'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);
    if (this.isRedesignedPanel) this.showSkeleton();

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  showSkeleton() {
    if (!this.resultsContainer) return;
    const row = `
      <div class="search-panel__skeleton-row">
        <div class="search-panel__skeleton-thumb"></div>
        <div class="search-panel__skeleton-lines">
          <div class="search-panel__skeleton-line"></div>
          <div class="search-panel__skeleton-line search-panel__skeleton-line--short"></div>
        </div>
      </div>`;
    this.resultsContainer.innerHTML = `
      <div class="search-panel__state search-panel__state--loading">
        <div class="search-panel__search-grid">
          <div class="search-panel__col search-panel__col--products">
            <div class="search-panel__product-list">${row.repeat(5)}</div>
          </div>
        </div>
      </div>`;
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => {
        this.toggleSearchState(false);
        this.collapseMobileSearch();
      });
    }
  }

  // Ikonet er en av/på-bryter: er feltet allerede felt ut, lukker et nytt
  // trykk hele greia igjen.
  toggleMobileSearch(event) {
    event.preventDefault();

    if (document.body.classList.contains(this.states.MOBILE_EXPANDED)) {
      this.input.blur();
      this.toggleSearchState(false);
      this.collapseMobileSearch();
      return;
    }

    document.body.classList.add(this.states.MOBILE_EXPANDED);
    this.mobileSearchToggle.setAttribute('aria-expanded', 'true');
    // Tvinger fram stil-/layoutoppdatering: et felt som nettopp lå bak
    // display:none kan ikke få fokus før nettleseren har regnet om at det
    // faktisk rendres nå.
    void this.input.offsetWidth;
    // focus() må skje synkront inne i klikk-handleren, ellers nekter iOS
    // Safari å åpne tastaturet (derfor ingen rAF her). Fokuset trigger
    // onFocus, som åpner panelet i klikk-tilstand akkurat som når feltet lå
    // permanent i headeren.
    this.input.focus();
  }

  collapseMobileSearch() {
    if (!this.mobileSearchToggle) return;
    document.body.classList.remove(this.states.MOBILE_EXPANDED);
    this.mobileSearchToggle.setAttribute('aria-expanded', 'false');
  }

  handleEscapeKey(event) {
    if (event.key !== 'Escape') return;
    this.input.blur();
    this.toggleSearchState(false);
    this.collapseMobileSearch();
    // Tastaturbrukere skal ikke bli stående på <body> etter å ha lukket.
    // Ikonet er display:none på desktop, og da gjør focus() ingenting.
    this.mobileSearchToggle?.focus();
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      if (!this.isRedesignedPanel) this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
